/**
 * Tab a3doc - Documentos de la gestoría (a3doc cloud, Wolters Kluwer).
 * Solo lectura: navega carpetas, lista documentos y permite ver/descargar.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { FolderOpen, Folder, FileText, Download, Eye, RefreshCw, ChevronRight, AlertTriangle, Home, Search } from 'lucide-react';
import { a3docAPI } from '../../api/api';

const A3docTab = () => {
  const [estado, setEstado] = useState(null);
  const [arbol, setArbol] = useState([]);
  const [carpetaActual, setCarpetaActual] = useState({ id: 1, name: 'Todos' });
  const [ruta, setRuta] = useState([{ id: 1, name: 'Todos' }]);
  const [documentos, setDocumentos] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [descargando, setDescargando] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  // Localiza una carpeta y sus hijas dentro del árbol
  const buscarNodo = useCallback((nodos, id) => {
    for (const n of nodos || []) {
      if (n.id === id) return n;
      const hijo = buscarNodo(n.children, id);
      if (hijo) return hijo;
    }
    return null;
  }, []);

  const cargarArbol = useCallback(async () => {
    try {
      const { data } = await a3docAPI.arbol();
      setArbol(data);
    } catch {
      setArbol([]);
    }
  }, []);

  const cargarDocumentos = useCallback(async (containerId) => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await a3docAPI.documentos(containerId, false, 300);
      setDocumentos(data.documentos || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudieron cargar los documentos');
      setDocumentos([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await a3docAPI.estado();
        setEstado(data);
        if (data.conectado) {
          cargarArbol();
          cargarDocumentos(1);
        }
      } catch {
        setEstado({ configurado: false });
      }
    })();
  }, [cargarArbol, cargarDocumentos]);

  const abrirCarpeta = (nodo) => {
    setBusqueda('');
    setCarpetaActual({ id: nodo.id, name: nodo.name });
    // Reconstruir ruta de migas: si está en la ruta actual, recortar; si no, añadir
    setRuta(prev => {
      const idx = prev.findIndex(r => r.id === nodo.id);
      if (idx >= 0) return prev.slice(0, idx + 1);
      return [...prev, { id: nodo.id, name: nodo.name }];
    });
    cargarDocumentos(nodo.id);
  };

  const descargarBlob = (blob, titulo, tipo) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${titulo}${tipo || ''}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const verDocumento = async (doc, descargar = false) => {
    setDescargando(doc.id);
    try {
      const { data: blob } = await a3docAPI.descargar(doc.id);
      if (descargar) {
        descargarBlob(blob, doc.titulo, doc.tipo);
      } else {
        const url = window.URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        // Si el navegador bloquea el pop-up, descargar el fichero como alternativa
        if (!win) {
          descargarBlob(blob, doc.titulo, doc.tipo);
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch (e) {
      // Surface del error real (incluido el JSON de error devuelto como blob)
      let detalle = e?.message || 'error desconocido';
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          detalle = JSON.parse(txt).detail || txt || detalle;
        } else if (e?.response?.status) {
          detalle = `HTTP ${e.response.status}`;
        }
      } catch { /* deja el mensaje por defecto */ }
      alert('No se pudo abrir el documento: ' + detalle);
    } finally {
      setDescargando(null);
    }
  };

  const nodoActual = carpetaActual.id === 1
    ? { children: arbol?.[0]?.children || arbol }
    : buscarNodo(arbol, carpetaActual.id);
  const subcarpetas = nodoActual?.children || [];

  const documentosFiltrados = busqueda.trim()
    ? documentos.filter(d => (d.titulo || '').toLowerCase().includes(busqueda.trim().toLowerCase()))
    : documentos;

  if (estado && !estado.configurado) {
    return (
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-800 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold mb-1">a3doc no está configurado</p>
          <p>Faltan las credenciales de a3doc en el servidor (A3DOC_CLIENTE / A3DOC_USUARIO / A3DOC_PASSWORD).</p>
        </div>
      </div>
    );
  }

  if (estado && estado.configurado && !estado.conectado) {
    return (
      <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-sm text-red-800 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold mb-1">No se pudo conectar con a3doc</p>
          <p>{estado.error || 'Revisa las credenciales.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {/* Cabecera */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Documentos de la gestoría</h3>
          {estado?.usuario && <p className="text-xs text-slate-400">Conectado como {estado.usuario} · a3doc cloud</p>}
        </div>
        <button
          onClick={() => { cargarArbol(); cargarDocumentos(carpetaActual.id); }}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${cargando ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      {/* Migas de pan */}
      <div className="flex items-center gap-1 text-sm text-slate-500 flex-wrap">
        {ruta.map((r, i) => (
          <span key={r.id} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
            <button
              onClick={() => abrirCarpeta(r)}
              className={`hover:text-slate-900 ${i === ruta.length - 1 ? 'font-semibold text-slate-900' : ''}`}
            >
              {i === 0 ? <Home className="w-4 h-4 inline" /> : r.name}
            </button>
          </span>
        ))}
      </div>

      {/* Subcarpetas */}
      {subcarpetas.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {subcarpetas.map(c => (
            <button
              key={c.id}
              onClick={() => abrirCarpeta(c)}
              className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-400 hover:shadow-sm transition text-left"
            >
              <Folder className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                <div className="text-xs text-slate-400">{c.numDocuments ?? 0} docs</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Buscador */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar documento por nombre…"
          className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
        />
      </div>

      {/* Documentos */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">{carpetaActual.name}</span>
          <span className="text-xs text-slate-400">
            · {busqueda ? `${documentosFiltrados.length} de ${total}` : total} documento(s)
          </span>
        </div>
        {cargando ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" /> Cargando…
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600 text-sm">{error}</div>
        ) : documentosFiltrados.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            {busqueda ? 'Ningún documento coincide con la búsqueda.' : 'No hay documentos en esta carpeta.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase border-b border-slate-100">
                <th className="px-4 py-2">Documento</th>
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2">Modificado</th>
                <th className="px-4 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {documentosFiltrados.map(d => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-slate-800">{d.titulo}</span>
                      <span className="text-xs text-slate-400 uppercase">{(d.tipo || '').replace('.', '')}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">{d.fecha || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-500">{(d.fecha_modificacion || '').split(' ')[0] || '—'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => verDocumento(d, false)}
                        disabled={descargando === d.id}
                        title="Ver"
                        className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded disabled:opacity-40"
                      >
                        {descargando === d.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => verDocumento(d, true)}
                        disabled={descargando === d.id}
                        title="Descargar"
                        className="p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded disabled:opacity-40"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default A3docTab;
